import { DateTime, Duration } from 'luxon';
import { kgToLbs } from './utils';
import { gqlTypes } from '../types/gqlTypes';

interface CommonPet {
  yearOfBirth: number;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
  weight: number;
}

export const dateOfBirth = (pet: CommonPet) =>
  DateTime.fromObject({
    year: pet.yearOfBirth,
    month: pet.monthOfBirth ?? undefined,
    day: pet.dayOfBirth ?? undefined,
  });

export function dayOfBirthString(pet: CommonPet) {
  let format: string;
  if (pet.dayOfBirth && pet.monthOfBirth) {
    format = `LLLL d, yyyy`;
  } else if (pet.monthOfBirth) {
    format = `LLLL, yyyy`;
  } else {
    // Only have year.
    format = `yyyy`;
  }

  return dateOfBirth(pet).toFormat(format);
}

export function ageString(pet: CommonPet) {
  const date = dateOfBirth(pet);
  const duration = DateTime.utc().diff(date);
  if (duration < Duration.fromObject({ years: 1 })) {
    const numMonths = Math.round(duration.as('months'));
    return numMonths < 1 ? `<1 month` : `${numMonths} month${numMonths > 1 ? 's' : ''}`;
  } else {
    const numYears = Math.floor(duration.as('years'));
    return `${numYears} yr${numYears > 1 ? 's' : ''}`;
  }
}

export function ageInMonths(year: number, month: number, day: number): number {
  return (
    (new Date().getFullYear() - year) * 12 + new Date().getMonth() - month + (new Date().getDate() >= day ? 0 : -1)
  );
}

export function roundedWeightInLbs(kg: number) {
  const lbs = kgToLbs(kg);
  const roundLbs = Math.max(1, Math.round(lbs));
  return roundLbs;
}

export interface PetPronouns {
  /** she / he / they -- for copy where the pet is the subject ("when she escapes") */
  subject: string;
  /** her / his / their */
  possessive: string;
  /** her / him / them */
  object: string;
  /**
   * True for they/them, where the verb has to agree: "they escape", not "they escapes".
   * Any sentence putting the pet in the subject position needs this.
   */
  usesPluralVerb: boolean;
}

/**
 * Gender is FEMALE | MALE in the schema but nullable on a pet, and registration does not
 * force a choice. Fall back to they/them rather than guessing, so copy stays correct for a
 * pet whose gender was never set.
 */
export function petPronouns(gender?: gqlTypes.Gender | null): PetPronouns {
  switch (gender) {
    case gqlTypes.Gender.FEMALE:
      return { subject: 'she', possessive: 'her', object: 'her', usesPluralVerb: false };
    case gqlTypes.Gender.MALE:
      return { subject: 'he', possessive: 'his', object: 'him', usesPluralVerb: false };
    default:
      return { subject: 'they', possessive: 'their', object: 'them', usesPluralVerb: true };
  }
}

/** dogs / cats / pets, for copy that makes a claim about other owners' animals. */
export function speciesPlural(species?: gqlTypes.Species | null): string {
  switch (species) {
    case gqlTypes.Species.DOG:
      return 'dogs';
    case gqlTypes.Species.CAT:
      return 'cats';
    default:
      return 'pets';
  }
}

export function weightString(pet: CommonPet) {
  if (pet.weight <= 0) {
    return '—';
  }
  const roundLbs = roundedWeightInLbs(pet.weight);
  return `${roundLbs} lbs`;
}
